import { miniSerializeError, SerializedError } from '@reduxjs/toolkit';
import { BaseQueryApi, BaseQueryFn, MaybePromise } from '@reduxjs/toolkit/query/react';
import { isBoolean } from 'lodash';
import { getUuid } from '../utils';
import { dismissLoader, showAlertDialog, showLoader } from './alertActions';
import { LoadingMessage } from './alertReducer';

export type CreateQueryFnOptions = {
    alertIfError?: boolean;
    pendingMessage?: boolean | string;
    onError?: (error: unknown) => void;
    onErrorAlert?: (error: SerializedError) => void;
    serializeError?: (error: unknown) => SerializedError;
};

type ExtraArgs = {
    _alertIfError?: boolean;
    _pendingMessage?: boolean | string;
};

type ArgsWithExtra<Args> = Args extends void
    ? void | ExtraArgs
    : Args extends Record<string, unknown>
      ? Args & ExtraArgs
      : Args | ({ _arg: Args } & ExtraArgs);

export const createQueryFn = <Args = void, Result = unknown, Error = unknown, ExtraOptions = object>(
    queryFn: (args: Args, queryApi: BaseQueryApi, extraOptions: ExtraOptions) => MaybePromise<Result | undefined>,
    options?: CreateQueryFnOptions,
): BaseQueryFn<ArgsWithExtra<Args | undefined>, Result, Error, ExtraOptions> => {
    return async (args, queryApi, extraOptions) => {
        let alertIfError: boolean | undefined = options?.alertIfError;
        let pendingMessage: boolean | string | undefined = options?.pendingMessage;

        if (args !== undefined) {
            const argsWithExtra = args as ExtraArgs;
            alertIfError = argsWithExtra._alertIfError ?? alertIfError;
            pendingMessage = argsWithExtra._pendingMessage ?? pendingMessage;
        }

        const loadingMessage: LoadingMessage | null = !pendingMessage
            ? null
            : {
                  id: getUuid(),
                  content: isBoolean(pendingMessage) ? 'Loading…' : pendingMessage,
              };

        try {
            if (loadingMessage) {
                queryApi.dispatch(showLoader(loadingMessage));
            }

            const argsWithExtra = args as { _arg?: Args } & ExtraArgs;
            const finalArgs = args !== undefined ? (argsWithExtra._arg ?? args) : args;
            const data = await queryFn(finalArgs as Args, queryApi, extraOptions);

            return {
                data: (data ?? null) as Result,
            };
        } catch (error) {
            if ((alertIfError === undefined && !!loadingMessage) || alertIfError) {
                const { serializeError, onErrorAlert } = options || {};

                const serializedError = serializeError?.(error) || miniSerializeError(error);
                if (onErrorAlert) {
                    onErrorAlert(serializedError);
                } else {
                    queryApi.dispatch(
                        showAlertDialog({
                            title: serializedError.name || '',
                            content: serializedError.formattedMessage || '',
                        }),
                    );
                }
            }

            options?.onError?.(error);

            return {
                error: error as Error,
            };
        } finally {
            if (loadingMessage) {
                queryApi.dispatch(dismissLoader(loadingMessage.id));
            }
        }
    };
};
